/**
 * @packageDocumentation
 * @module      System.ts
 * @description Encapsulates a system and its devices.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
import DeviceInfo from "./DeviceInfo";
import IpAddressMap from "./IpAddressMap";
export default class System {
    constructor(systemId, systemName, associationState, created, settings, devices) {
        this._devices = [];
        this._systemId = systemId || '';
        this.systemName = systemName || '';
        this.associationState = associationState || 'NONE';
        this.created = created || new Date();
        this.settings = settings || {};
        this.version = '';
        this.hospitalName = '';
        this.address = '';
        this.lastHeard = null;
        this.missingSince = null;
        this.enabled = true;
        this.fit = true;
        this.devices = devices || [];
    }
    get systemId() {
        return this._systemId;
    }
    set systemId(newValue) {
        this._systemId = newValue;
        this.updateDeviceSystemIds();
    }
    get devices() {
        return this._devices;
    }
    set devices(ary) {
        this._devices = ary;
        this.updateDeviceSystemIds();
    }
    /**
     * Get the computed summary state of the system.
     * Unfortunately, this duplicates the getSystemState method in the Cloud API's System.java but this allows us
     * to update the systemState dynamically on the System card as the user toggles the Enable switch.
     */
    get systemState() {
        if (this.associationState != 'NONE') {
            return this.associationState;
        }
        if (!this.enabled) {
            return 'DISABLED';
        }
        if (!!this.missingSince) {
            return 'MISSING';
        }
        return this.fit ? 'AVAILABLE' : 'UNAVAILABLE';
    }
    toJSON() {
        const jsonObj = Object.assign({}, this);
        jsonObj.systemId = this.systemId;
        delete jsonObj._systemId;
        jsonObj.created = this.created.toISOString();
        jsonObj.devices = this.devices.map((d) => d.toJSON());
        delete jsonObj._devices;
        return jsonObj;
    }
    /**
     * Return a System object, with devices, built from a JSON object that might be returned from the server.
     *
     * @param json JSON representation of a System and its devices.
     */
    static fromJSON(json) {
        let sys = new System();
        sys.systemId = json.systemId;
        sys.systemName = json.systemName;
        sys.associationState = json.associationState;
        sys.created = new Date(Date.parse(json.created));
        sys.settings = json.settings;
        sys.version = json.version;
        sys.hospitalName = json.hospitalName;
        sys.address = json.address;
        sys.lastHeard = !!json.lastHeard ? new Date(Date.parse(json.lastHeard)) : null;
        sys.missingSince = !!json.missingSince ? new Date(Date.parse(json.missingSince)) : null;
        sys.enabled = json.enabled;
        sys.fit = json.fit;
        for (let dev of json.devices) {
            sys.devices.push(DeviceInfo.fromJSON(dev));
        }
        return sys;
    }
    findDevice(endsWith) {
        var _a;
        return (_a = this.devices) === null || _a === void 0 ? void 0 : _a.find((d) => d.deviceType.endsWith(endsWith));
    }
    /**
     * Return the UI device (CSUI or PSUI), or undefined if it does not exist.
     */
    get ui() {
        return this.findDevice("UI");
    }
    /**
     * Return the IC device (CSIC or PSIC), or undefined if it does not exist.
     */
    get ic() {
        return this.findDevice("IC");
    }
    /**
     * Return the RC device (CSRC or PSRC), or undefined if it does not exist.
     */
    get rc() {
        return this.findDevice("RC");
    }
    /**
     * Set the system to be control-side or patient-side, depending on the parameter, and create any missing devices.
     * Also ensures that all devices' private IP addresses are correct.
     *
     * @param isControl True to set to control-side; false to set to patient-side
     */
    set isControlSide(isControl) {
        const setDevice = (devProp) => {
            const devStr = (isControl ? "CS" : "PS") + devProp.toUpperCase();
            // @ts-ignore implicit 'any' type
            if (this[devProp] === undefined) {
                this.devices.push(new DeviceInfo(this.systemId, devStr));
            }
            else {
                // @ts-ignore implicit 'any' type
                this[devProp].deviceType = devStr;
            }
            // @ts-ignore implicit 'any' type
            this[devProp].privateVpnIpv4 = IpAddressMap.getIpAddress(devStr);
        };
        setDevice("ui");
        setDevice("ic");
        setDevice("rc");
    }
    /**
     * Return true if the first device in the system is control-side; false if there are no devices or if
     * the first one is patient-side.
     *
     * Note that the possibility of an inconsistent set of devices is ignored. See HITALPHA-869.
     */
    get isControlSide() {
        return this.devices.length > 0 && this.devices[0].deviceType.startsWith("C");
    }
    /**
     * Return true if the first device in the system is patient-side; false if there are no devices or if
     * the first one is control-side.
     *
     * Note that the possibility of an inconsistent set of devices is ignored. See HITALPHA-869.
     */
    get isPatientSide() {
        return this.devices.length > 0 && this.devices[0].deviceType.startsWith("P");
    }
    /**
     * Return true if we don't know what side this system is, because it has no devices; false otherwise.
     */
    get isUnknownSide() {
        return this.devices.length === 0;
    }
    /**
     * Return the "side" of a system as a string suitable for display
     */
    get systemSideString() {
        if (this.isControlSide) {
            return 'Control-side';
        }
        else if (this.isPatientSide) {
            return 'Patient-side';
        }
        return 'Unknown';
    }
    /**
     * Enable or disable a system.
     *
     * @param wantEnable - True to enable, false to disable.
     */
    enable(wantEnable = true) {
        this.enabled = wantEnable;
    }
    get isEnabled() {
        return this.enabled;
    }
    updateDeviceSystemIds() {
        this.devices.forEach((d) => {
            d.systemId = this.systemId;
        });
    }
}
//# sourceMappingURL=System.js.map